import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import NotificationItem from "../components/NotificationItem";
import { Logos } from "../assets/logos";
import notificationService from "../services/notificationService";

/* ===================================================== */
/* 🔔 NOTIFICATION TYPES                                 */
/* ===================================================== */

const TYPE_OPTIONS = [
  "all",
  "task",
  "milestone",
  "investor",
  "system",
];

/* ===================================================== */
/* 🔧 HELPERS                                            */
/* ===================================================== */

function getNotificationId(notification) {
  return String(
    notification?._id ||
      notification?.id ||
      ""
  );
}

function getNotificationReadState(notification) {
  if (typeof notification?.isRead === "boolean") {
    return notification.isRead;
  }

  /*
   * Backward compatibility for any existing API response
   * that may still expose one of these properties.
   */
  if (typeof notification?.read === "boolean") {
    return notification.read;
  }

  if (typeof notification?.seen === "boolean") {
    return notification.seen;
  }

  return false;
}

/* ===================================================== */
/* 📄 PAGE                                               */
/* ===================================================== */

export default function Notifications() {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);

  // Filters
  const [query, setQuery] = useState("");
  const [type, setType] = useState("all");
  const [onlyUnread, setOnlyUnread] = useState(false);

  /* ===================================================== */
  /* 📦 FETCH                                              */
  /* ===================================================== */

  const fetchNotifications = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
      }

      setError("");

      try {
        const notifications =
          await notificationService.listNotifications();

        setItems(
          Array.isArray(notifications)
            ? notifications
            : []
        );
      } catch (err) {
        console.error(
          "[Notifications] Failed to fetch notifications:",
          err
        );

        setError(
          err?.message ||
            "Failed to load notifications."
        );

        /*
         * Do not destroy already-loaded data when a
         * manual refresh fails.
         */
        if (!silent) {
          setItems([]);
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const notifications =
          await notificationService.listNotifications();

        if (!mounted) return;

        setItems(
          Array.isArray(notifications)
            ? notifications
            : []
        );
      } catch (err) {
        if (!mounted) return;

        console.error(
          "[Notifications] Initial fetch failed:",
          err
        );

        setError(
          err?.message ||
            "Failed to load notifications."
        );

        setItems([]);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  /* ===================================================== */
  /* 🔐 MARK SINGLE READ                                  */
  /* ===================================================== */

  const handleMarkRead = useCallback(
    async (notificationId) => {
      if (!notificationId || actionLoading) {
        return;
      }

      const id = getNotificationId({
        _id: notificationId,
      });

      if (!id) return;

      const target = items.find(
        (notification) =>
          getNotificationId(notification) === id
      );

      if (!target || getNotificationReadState(target)) {
        return;
      }

      setActionLoading(true);
      setError("");

      try {
        const response =
          await notificationService.markNotificationRead(
            id
          );

        /*
         * Update the UI immediately using the successful
         * server operation.
         */
        setItems((currentItems) =>
          currentItems.map((notification) =>
            getNotificationId(notification) === id
              ? {
                  ...notification,
                  isRead:
                    response?.notification?.isRead ??
                    response?.data?.isRead ??
                    true,
                }
              : notification
          )
        );
      } catch (err) {
        console.error(
          "[Notifications] Failed to mark notification read:",
          err
        );

        setError(
          err?.message ||
            "Failed to mark notification as read."
        );
      } finally {
        setActionLoading(false);
      }
    },
    [actionLoading, items]
  );

  /* ===================================================== */
  /* 👁 VIEW NOTIFICATION                                 */
  /* ===================================================== */

  const handleView = useCallback(
    async (notification) => {
      const id = getNotificationId(notification);

      if (!id || getNotificationReadState(notification)) {
        return;
      }

      await handleMarkRead(id);
    },
    [handleMarkRead]
  );

  /* ===================================================== */
  /* ✅ MARK ALL READ                                     */
  /* ===================================================== */

  const handleMarkAllRead = useCallback(async () => {
    const unreadExists = items.some(
      (notification) =>
        !getNotificationReadState(notification)
    );

    if (!unreadExists || actionLoading) {
      return;
    }

    setActionLoading(true);
    setError("");

    try {
      const response =
        await notificationService.markAllNotificationsRead();

      /*
       * Update all currently loaded notifications locally
       * only after the backend operation succeeds.
       */
      setItems((currentItems) =>
        currentItems.map((notification) => ({
          ...notification,
          isRead:
            response?.notification?.isRead ??
            response?.data?.isRead ??
            true,
        }))
      );
    } catch (err) {
      console.error(
        "[Notifications] Failed to mark all notifications read:",
        err
      );

      setError(
        err?.message ||
          "Failed to mark all notifications as read."
      );
    } finally {
      setActionLoading(false);
    }
  }, [actionLoading, items]);

  /* ===================================================== */
  /* 🔍 FILTERING                                         */
  /* ===================================================== */

  const filteredNotifications = useMemo(() => {
    const normalizedQuery = query
      .trim()
      .toLowerCase();

    return items.filter((notification) => {
      const notificationType = String(
        notification?.type || "system"
      ).toLowerCase();

      if (
        type !== "all" &&
        notificationType !== type
      ) {
        return false;
      }

      if (
        onlyUnread &&
        getNotificationReadState(notification)
      ) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const title = String(
        notification?.title || ""
      ).toLowerCase();

      const message = String(
        notification?.message || ""
      ).toLowerCase();

      return (
        title.includes(normalizedQuery) ||
        message.includes(normalizedQuery)
      );
    });
  }, [
    items,
    onlyUnread,
    query,
    type,
  ]);

  /* ===================================================== */
  /* 📊 COUNTS                                            */
  /* ===================================================== */

  const unreadCount = useMemo(
    () =>
      items.reduce(
        (count, notification) =>
          count +
          (getNotificationReadState(notification)
            ? 0
            : 1),
        0
      ),
    [items]
  );

  const hasNotifications = items.length > 0;
  const hasFilteredNotifications =
    filteredNotifications.length > 0;

  /* ===================================================== */
  /* 🖥 UI                                                */
  /* ===================================================== */

  return (
    <div className="p-6 max-w-5xl">
      {/* ================================================= */}
      {/* HEADER                                            */}
      {/* ================================================= */}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3">
          <img
            src={Logos.black.square}
            alt="HeadwaySpot"
            className="h-8 w-8"
          />

          <div>
            <h1 className="text-2xl font-semibold">
              Notifications
            </h1>

            {unreadCount > 0 && (
              <p className="mt-0.5 text-sm text-gray-500">
                {unreadCount} unread{" "}
                {unreadCount === 1
                  ? "notification"
                  : "notifications"}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() =>
              fetchNotifications()
            }
            disabled={loading || actionLoading}
            className="rounded-lg border px-3 py-2 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>

          <button
            type="button"
            onClick={handleMarkAllRead}
            disabled={
              actionLoading ||
              unreadCount === 0
            }
            className="rounded-lg border px-3 py-2 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {actionLoading
              ? "Updating…"
              : "Mark all read"}
          </button>
        </div>
      </div>

      {/* ================================================= */}
      {/* FILTERS                                           */}
      {/* ================================================= */}

      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border bg-white p-4">
        <input
          type="search"
          value={query}
          onChange={(event) =>
            setQuery(event.target.value)
          }
          placeholder="Search notifications…"
          aria-label="Search notifications"
          className="w-full sm:w-56 rounded-lg border px-3 py-2 text-sm outline-none focus:border-gray-400"
        />

        <select
          value={type}
          onChange={(event) =>
            setType(event.target.value)
          }
          aria-label="Filter by notification type"
          className="rounded-lg border px-3 py-2 text-sm outline-none focus:border-gray-400"
        >
          {TYPE_OPTIONS.map(
            (option) => (
              <option
                key={option}
                value={option}
              >
                {option === "all"
                  ? "All Types"
                  : option
                      .charAt(0)
                      .toUpperCase() +
                    option.slice(1)}
              </option>
            )
          )}
        </select>

        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={onlyUnread}
            onChange={(event) =>
              setOnlyUnread(
                event.target.checked
              )
            }
            className="h-4 w-4"
          />

          Unread only
        </label>
      </div>

      {/* ================================================= */}
      {/* ERROR                                             */}
      {/* ================================================= */}

      {error && (
        <div
          role="alert"
          className="mb-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"
        >
          {error}
        </div>
      )}

      {/* ================================================= */}
      {/* LIST                                              */}
      {/* ================================================= */}

      <div className="rounded-2xl border bg-white">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">
            Loading notifications…
          </div>
        ) : !hasNotifications ? (
          <div className="p-6 text-sm text-gray-500">
            No notifications found.
          </div>
        ) : !hasFilteredNotifications ? (
          <div className="p-6 text-sm text-gray-500">
            No notifications match your filters.
          </div>
        ) : (
          <ul className="divide-y">
            {filteredNotifications.map(
              (notification) => {
                const id =
                  getNotificationId(
                    notification
                  );

                return (
                  <NotificationItem
                    key={id}
                    notification={
                      notification
                    }
                    isRead={getNotificationReadState(
                      notification
                    )}
                    onMarkRead={
                      handleMarkRead
                    }
                    onView={
                      handleView
                    }
                  />
                );
              }
            )}
          </ul>
        )}
      </div>
    </div>
  );
}